import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiStar } from 'react-icons/fi';
import { theme } from '../styles/theme';
import ImageModal from './veiculos/ImageModal';
import api from '../services/api';

const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${theme.colors.background.secondary};

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.primary[50]};
  }

  &.drag-over {
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.primary[100]};
  }
`;

const ImageUploadText = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: ${theme.spacing[2]};
`;

const ImageUploadSubtext = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[4]};
`;

const ImagePreviewItem = styled.div`
  position: relative;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  aspect-ratio: 1;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: ${theme.spacing[1]};
  right: ${theme.spacing[1]};
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const FavoriteButton = styled.button`
  position: absolute;
  top: ${theme.spacing[1]};
  left: ${theme.spacing[1]};
  background: ${props => props.$isFavorite ? 'rgba(255, 193, 7, 0.95)' : 'rgba(0, 0, 0, 0.7)'};
  color: ${props => props.$isFavorite ? '#000' : 'white'};
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    background: ${props => props.$isFavorite ? 'rgba(255, 193, 7, 1)' : 'rgba(255, 193, 7, 0.8)'};
    color: #000;
    transform: scale(1.1);
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing[2]};
  font-size: ${theme.fontSizes.sm};
`;

const ImageUploader = ({ 
  label = "Imagens", 
  maxSize = 3 * 1024 * 1024, // 3MB por padrão
  acceptedTypes = "image/*",
  onImagesChange,
  initialImages = [],
  maxImages = null,
  showPreview = true,
  uploadText = "📸 Clique aqui ou arraste imagens para fazer upload",
  uploadSubtext = "Máximo 3MB por imagem • Formatos aceitos: JPG, PNG, GIF, WebP",
  // Novas props para integração com API
  veiculoId = null,
  idCliente = null,
  onUploadSuccess = null,
  onUploadError = null,
  // Prop para callback quando favorito muda
  onFavoriteChange = null
}) => {
  const [imagens, setImagens] = useState(initialImages);
  const [dragOver, setDragOver] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [settingFavorite, setSettingFavorite] = useState(false);

  // Atualizar imagens quando initialImages mudar
  useEffect(() => {
    setImagens(initialImages);
  }, [initialImages]);

  // Função para validar arquivo de imagem
  const validateImageFile = (file) => {
    // Verificar se é uma imagem
    if (!file.type.startsWith('image/')) {
      alert('Apenas arquivos de imagem são permitidos.');
      return false;
    }
    
    // Verificar tamanho
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      alert(`O arquivo deve ter no máximo ${maxSizeMB}MB.`);
      return false;
    }
    
    return true;
  };

  // Função para processar upload de imagens
  const handleImageUpload = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(validateImageFile);
    
    if (validFiles.length !== fileArray.length) {
      return; // Alguns arquivos foram rejeitados
    }

    // Verificar limite de imagens
    if (maxImages && (imagens.length + validFiles.length) > maxImages) {
      alert(`Máximo de ${maxImages} imagens permitidas.`);
      return;
    }

    // Se não há veiculoId, usar método local (preview apenas)
    if (!veiculoId) {
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const novaImagem = {
            id: Date.now() + Math.random(),
            file: file,
            preview: e.target.result,
            name: file.name
          };
          
          setImagens(prev => {
            const novasImagens = [...prev, novaImagem];
            // Chamar callback se fornecido
            if (onImagesChange) {
              onImagesChange(novasImagens);
            }
            return novasImagens;
          });
        };
        reader.readAsDataURL(file);
      });
      return;
    }

    // Upload para servidor
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('imagens', file);
      });
      
      if (idCliente) {
        formData.append('id_cliente', idCliente);
      }

      console.log('📸 Frontend - Enviando imagens para servidor...');
      console.log('📸 Frontend - Veículo ID:', veiculoId);
      console.log('📸 Frontend - Cliente ID:', idCliente);
      console.log('📸 Frontend - Arquivos:', validFiles.length);

      const response = await api.post(`/veiculos/${veiculoId}/imagens`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      console.log('✅ Frontend - Upload realizado com sucesso:', response.data);

      if (response.data.success) {
        // Converter imagens do servidor para formato local
        const uploadedImages = response.data.data.map(img => ({
          id: img.id,
          preview: img.link,
          name: img.name,
          size: 0, // Não temos o tamanho do servidor
          type: 'image/jpeg' // Assumir JPEG
        }));

        // Atualizar estado local
        const novasImagens = [...imagens, ...uploadedImages];
        setImagens(novasImagens);
        
        if (onImagesChange) {
          onImagesChange(novasImagens);
        }

        if (onUploadSuccess) {
          onUploadSuccess(response.data.data);
        }

        console.log('📸 Frontend - Imagens adicionadas ao estado local:', uploadedImages.length);
      }

    } catch (error) {
      console.error('❌ Frontend - Erro no upload:', error);
      
      if (onUploadError) {
        onUploadError(error);
      } else {
        alert('Erro ao fazer upload das imagens. Tente novamente.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handler para mudança no input de arquivo
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleImageUpload(files);
    }
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    e.target.value = '';
  };

  // Handlers para drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageUpload(files);
    }
  };

  // Função para definir imagem como favorita
  const setFavorite = async (imageId) => {
    // Prevenir múltiplos cliques enquanto está processando
    if (settingFavorite) return;

    // Se a imagem já é favorita, não fazer nada
    const imagemAtual = imagens.find(img => img.id === imageId);
    if (imagemAtual?.favorita) return;

    // Se há veiculoId, atualizar no servidor
    if (veiculoId) {
      try {
        setSettingFavorite(true);
        console.log('⭐ Frontend - Definindo imagem como favorita:', imageId);
        await api.put(`/veiculos/${veiculoId}/imagens/${imageId}/favorita`);
        console.log('✅ Frontend - Imagem definida como favorita no servidor');
      } catch (error) {
        console.error('❌ Frontend - Erro ao definir favorita no servidor:', error);
        alert('Erro ao definir imagem como favorita. Tente novamente.');
        setSettingFavorite(false);
        return;
      } finally {
        setSettingFavorite(false);
      }
    }

    // Atualizar estado local - remover favorita de todas e definir na selecionada
    setImagens(prev => {
      const novasImagens = prev.map(img => ({
        ...img,
        favorita: img.id === imageId
      }));
      
      // Chamar callback se fornecido
      if (onImagesChange) {
        onImagesChange(novasImagens);
      }
      
      if (onFavoriteChange) {
        onFavoriteChange(imageId);
      }
      
      return novasImagens;
    });
  };

  // Função para remover imagem
  const removeImage = async (imageId) => {
    // Se há veiculoId e a imagem tem ID numérico (vem do servidor), remover do servidor
    if (veiculoId && typeof imageId === 'string' && imageId.length > 10) {
      try {
        console.log('🗑️ Frontend - Removendo imagem do servidor:', imageId);
        await api.delete(`/veiculos/${veiculoId}/imagens/${imageId}`);
        console.log('✅ Frontend - Imagem removida do servidor');
      } catch (error) {
        console.error('❌ Frontend - Erro ao remover imagem do servidor:', error);
        // Continuar com remoção local mesmo se falhar no servidor
      }
    }

    // Remover do estado local
    setImagens(prev => {
      const novasImagens = prev.filter(img => img.id !== imageId);
      // Chamar callback se fornecido
      if (onImagesChange) {
        onImagesChange(novasImagens);
      }
      return novasImagens;
    });
  };

  // Handler para clique na área de upload
  const handleUploadAreaClick = () => {
    document.getElementById(`image-upload-${label.toLowerCase().replace(/\s+/g, '-')}`).click();
  };

  // Handler para clique na imagem
  const handleImageClick = (index) => {
    console.log('🖼️ Clique na imagem:', index, 'Total de imagens:', imagens.length);
    console.log('🖼️ Imagem clicada:', imagens[index]);
    setCurrentImageIndex(index);
    setModalOpen(true);
  };

  // Handler para fechar modal
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <ImageUploadContainer>
      <Label>{label}</Label>
      <ImageUploadArea
        className={dragOver ? 'drag-over' : ''}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleUploadAreaClick}
      >
        <ImageUploadText>
          {uploadText}
        </ImageUploadText>
        <ImageUploadSubtext>
          {uploadSubtext}
        </ImageUploadSubtext>
        <input
          id={`image-upload-${label.toLowerCase().replace(/\s+/g, '-')}`}
          type="file"
          multiple
          accept={acceptedTypes}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </ImageUploadArea>

      {/* Indicador de progresso de upload */}
      {uploading && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <div style={{ 
            background: '#f0f0f0', 
            borderRadius: '8px', 
            height: '8px', 
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <div style={{
              background: '#007bff',
              height: '100%',
              width: `${uploadProgress}%`,
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Enviando imagens... {uploadProgress}%
          </div>
        </div>
      )}

      {showPreview && imagens.length > 0 && (
        <ImagePreviewGrid>
          {imagens.map((imagem, index) => (
            <ImagePreviewItem key={imagem.id}>
              <ImagePreview 
                src={imagem.preview} 
                alt={imagem.name}
                onClick={() => handleImageClick(index)}
                style={{ cursor: 'pointer' }}
              />
              <FavoriteButton 
                $isFavorite={imagem.favorita}
                onClick={(e) => {
                  e.stopPropagation();
                  setFavorite(imagem.id);
                }}
                title={imagem.favorita ? 'Imagem principal' : 'Definir como imagem principal'}
                disabled={settingFavorite}
              >
                <FiStar style={{ fill: imagem.favorita ? '#000' : 'none' }} />
              </FavoriteButton>
              <RemoveImageButton onClick={() => removeImage(imagem.id)}>
                ×
              </RemoveImageButton>
            </ImagePreviewItem>
          ))}
        </ImagePreviewGrid>
      )}

      {/* Modal para exibir imagens maximizadas */}
      <ImageModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        images={imagens.map(img => ({ link: img.preview, alt: img.name }))}
        initialIndex={currentImageIndex}
      />
    </ImageUploadContainer>
  );
};

export default ImageUploader;
